import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Image as ImageIcon } from "lucide-react";
import { PILLARS, PILLARS_SECTION } from "@/lib/content";
import { Reveal, EASE } from "./Reveal";
import { renderAA } from "./aa";
import SemanticCoreVisual from "./SemanticCoreVisual";
import AIAgentsVisual from "./AIAgentsVisual";
import WorkflowsApplicationsVisual from "./WorkflowsApplicationsVisual";
import AIFDEVisual from "./AIFDEVisual";
import AITrismVisual from "./AITrismVisual";

const CORE = PILLARS.slice(0, 3);
const FDE = PILLARS[3];
const TRISM = PILLARS[4];
const DOCK_ITEMS = [...CORE, FDE, TRISM];

export const Pillars = () => {
  const [active, setActive] = useState(DOCK_ITEMS.indexOf(FDE));
  const cap = DOCK_ITEMS[active];
  const dark = cap.title === FDE.title;
  const trism = cap.title === TRISM.title;

  // Stage theming: dark = FDE moat, trism = gold governance layer, else light pillar
  const stageBg = dark
    ? "bg-[#211307] border-white/10"
    : trism
    ? "bg-[#FBF2E1] border-[#E0902B]/60"
    : "bg-white border-[#3E2818]/10";
  const headText = dark ? "text-[#F7F5F0]" : "text-[#3E2818]";
  const subText = dark ? "text-white/70" : "text-[#3E2818]/65";
  const phBorder = dark
    ? "border-[#FFB84D]/35 bg-white/[0.03]"
    : trism
    ? "border-[#8A5730]/40 bg-white/50"
    : "border-[#8A5730]/35 bg-[#F7F5F0]/70";
  const iconBox = dark
    ? "bg-white/10 text-[#FFB84D]"
    : trism
    ? "bg-[#E0902B]/25 text-[#8A5730]"
    : "bg-[#8A5730]/10 text-[#8A5730]";
  const phLabel = dark ? "text-[#FFB84D]" : "text-[#8A5730]";
  const phSub = dark ? "text-white/45" : "text-[#3E2818]/45";
  const cardBg = dark
    ? "bg-white/[0.06] border-white/10"
    : trism
    ? "bg-[#FFFDF7] border-[#E0902B]/40"
    : "bg-[#F7F5F0] border-[#3E2818]/8";
  const featTitle = dark ? "text-[#F7F5F0]" : "text-[#3E2818]";
  const featDesc = dark ? "text-white/55" : "text-[#3E2818]/55";

  return (
    <section id="platform" className="bg-[#F7F5F0] py-14 lg:py-16" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="max-w-6xl mb-8 lg:mb-10">
          <Reveal delay={0.08}>
            <h2
              className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] text-[#3E2818] leading-[1.12] max-w-4xl"
              data-testid="pillars-title"
            >
              {renderAA(PILLARS_SECTION.title)}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-4 text-base text-[#3E2818]/65 leading-relaxed max-w-2xl">{PILLARS_SECTION.intro}</p>
          </Reveal>
        </div>

        {/* Interactive dock + stage */}
        <Reveal delay={0.1}>
          <div className="grid lg:grid-cols-12 gap-4 lg:gap-5 lg:h-[540px]">
            {/* Dock */}
            <div className="lg:col-span-4 flex flex-col gap-2">
              {DOCK_ITEMS.map((c, i) => {
                const isFde = c.title === FDE.title;
                const isTrism = c.title === TRISM.title;
                const on = i === active;
                return (
                  <button
                    key={c.title}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => setActive(i)}
                    data-testid={`platform-dock-item-${i}`}
                    className={`flex-1 text-left rounded-xl px-4 py-3 lg:py-2.5 border transition-all duration-300 flex items-center justify-between group ${
                      isFde
                        ? `bg-[#3E2818] text-[#F7F5F0] ${on ? "border-[#FFB84D]" : "border-[#3E2818]"}`
                        : isTrism
                        ? `bg-[#FBF2E1] ${on ? "border-[#E0902B]/70 shadow-[0_10px_30px_-18px_rgba(224,144,43,0.5)]" : "border-[#E0902B]/45 hover:border-[#E0902B]/70"}`
                        : on
                        ? "bg-white border-[#8A5730]/45 shadow-[0_10px_30px_-18px_rgba(62,40,24,0.5)]"
                        : "bg-white/60 border-[#3E2818]/10 hover:border-[#8A5730]/30"
                    }`}
                  >
                    <div>
                      <span className={`font-mono2 text-[9px] uppercase tracking-[0.2em] ${isFde ? "text-[#FFB84D]" : "text-[#8A5730]"}`}>
                        {c.kicker}
                      </span>
                      <h3 className={`font-display text-[17px] lg:text-lg font-medium leading-tight ${isFde ? "text-[#F7F5F0]" : "text-[#3E2818]"}`}>
                        {c.title}
                      </h3>
                    </div>
                    <ArrowRight
                      size={16}
                      className={`shrink-0 transition-all duration-300 ${isFde ? "text-[#FFB84D]" : "text-[#8A5730]"} ${
                        on ? "translate-x-0 opacity-100" : "-translate-x-1 opacity-0 group-hover:opacity-70"
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {/* Stage */}
            <div
              className={`lg:col-span-8 rounded-2xl border px-6 py-6 lg:px-8 lg:py-6 flex flex-col min-h-[440px] transition-colors duration-500 ${stageBg}`}
              data-testid="platform-stage"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={cap.title}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="flex flex-col h-full"
                >
                  <h3 className={`font-display text-2xl lg:text-3xl font-medium tracking-tight leading-tight ${headText}`}>{cap.title}</h3>
                  <p className={`text-sm mt-1.5 max-w-xl leading-relaxed ${subText}`}>{cap.promise}</p>

                  {/* Per-capability visual */}
                  {cap.title === "Semantic Core" ? (
                    <div className="flex-1 min-h-[160px] mt-3 mb-4 rounded-xl bg-[#F7F5F0] border border-[#8A5730]/20 overflow-hidden flex items-center justify-center" data-testid="platform-visual-semantic-core">
                      <SemanticCoreVisual className="w-full h-full max-h-full" preserveAspectRatio="xMidYMid meet" />
                    </div>
                  ) : cap.title === "AI Agents" ? (
                    <div className="flex-1 min-h-[160px] mt-3 mb-4 rounded-xl bg-[#F7F5F0] border border-[#8A5730]/20 overflow-hidden flex items-center justify-center" data-testid="platform-visual-ai-agents">
                      <AIAgentsVisual className="w-full h-full max-h-full" preserveAspectRatio="xMidYMid meet" />
                    </div>
                  ) : cap.title === "Intelligent Applications" ? (
                    <div className="flex-1 min-h-[160px] mt-3 mb-4 rounded-xl bg-[#F7F5F0] border border-[#8A5730]/20 overflow-hidden flex items-center justify-center px-4 py-3" data-testid="platform-visual-intelligent-applications">
                      <WorkflowsApplicationsVisual className="w-full" />
                    </div>
                  ) : cap.title === "AI Forward Deployed Engineer" ? (
                    <div className="flex-1 min-h-[160px] mt-3 mb-4 rounded-xl bg-[#2B1E15] border border-white/10 overflow-hidden flex items-center justify-center" data-testid="platform-visual-ai-fde">
                      <AIFDEVisual className="w-full" />
                    </div>
                  ) : cap.title === "AI TRiSM" ? (
                    <div className="flex-1 min-h-[160px] mt-3 mb-4 rounded-xl bg-[#FFFDF7] border border-[#E0902B]/40 overflow-hidden flex items-center justify-center" data-testid="platform-visual-ai-trism">
                      <AITrismVisual className="w-full" />
                    </div>
                  ) : (
                  <div
                    className={`flex-1 min-h-[160px] mt-3 mb-4 rounded-xl border border-dashed flex flex-col items-center justify-center text-center px-6 ${phBorder}`}
                    data-testid="platform-visual-placeholder"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${iconBox}`}>
                      <ImageIcon size={18} strokeWidth={1.8} />
                    </div>
                    <p className={`font-mono2 text-[10px] uppercase tracking-[0.25em] ${phLabel}`}>Visual placeholder</p>
                    <p className={`text-[12px] mt-1.5 max-w-xs leading-snug ${phSub}`}>
                      {cap.title} — diagram, animation or product shot goes here
                    </p>
                  </div>
                  )}

                  <div className="grid sm:grid-cols-3 gap-3 sm:h-[148px]">
                    {cap.features.map((f) => (
                      <div key={f.title} className={`rounded-xl p-3 border overflow-hidden ${cardBg}`}>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-1.5 ${iconBox}`}>
                          <f.icon size={14} strokeWidth={1.9} />
                        </div>
                        <p className={`text-[13px] font-semibold leading-snug ${featTitle}`}>{f.title}</p>
                        <p className={`text-[11px] mt-1 leading-[1.25] ${featDesc}`}>{f.desc}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
